import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BadgeCheck, CircleCheck, Info, Printer, Sheet, TriangleAlert } from 'lucide-react';
import {
  CLASSIFICATION_LABEL,
  Classification,
  IDC_EXCLUSION_LABEL,
  PERIOD_STATUS_LABEL,
  PeriodStatus,
  RNC_SEVERITY_LABEL,
  UserRole,
  type Competence,
  type EvaluationReportDto,
  type ReportTrailDto,
  type PeriodStatus as PeriodStatusType,
  type SnapshotItemDto,
} from '@weloze/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { usePageLabel } from '../lib/navigation';
import { formatDate, formatDateTime, formatIndex, formatDecimal } from '../lib/format';
import { Alert, Badge, Button, Card, Select, Skeleton } from '../components/ui';

interface CompetenceOption {
  competence: Competence;
  label: string;
  status: PeriodStatusType;
}

/**
 * As colunas do CSV do IDC.
 *
 * Exporta os **itens**, e não os índices: quem abre isso no Excel quer conferir
 * linha a linha, e os índices já estão na tela e no PDF. Separador `;` e BOM
 * porque o Excel em pt-BR lê `,` como decimal — sem isso a planilha abre com
 * tudo numa coluna só.
 */
/** Quebra de linha e BOM que o Excel do Windows espera num CSV. */
const CRLF = String.fromCharCode(13, 10);
const BOM = String.fromCharCode(0xfeff);

function baixarCsv(report: EvaluationReportDto): void {
  const linhas = [
    ['Pedido', 'Item', 'Produto', 'Descrição', 'Data prevista', 'Data de entrega',
     'Origem da data', 'Diferença', 'No prazo', 'Conta no IDC', 'Motivo', 'Notas fiscais'],
    ...[...report.idc.counted, ...report.idc.excluded].map((item) => [
      item.orderNumber,
      item.orderItem,
      item.productCode,
      item.productDesc ?? '',
      formatDate(item.expectedDate),
      formatDate(item.deliveryDate),
      item.deliveryDateSource === 'DTDIGIT' ? 'Digitação da nota' : 'Informada na nota',
      item.diffLabel,
      item.onTime === null ? '' : item.onTime ? 'Sim' : 'Não',
      item.countsForIdc ? 'Sim' : 'Não',
      (item.exclusionReason && IDC_EXCLUSION_LABEL[item.exclusionReason]) || '',
      item.invoiceRefs.join(' '),
    ]),
  ];

  const csv = linhas
    .map((linha) => linha.map((celula) => `"${String(celula).replace(/"/g, '""')}"`).join(';'))
    .join(CRLF);

  // O BOM é o que faz o Excel reconhecer UTF-8. Sem ele, todo acento vira
  // caractere estranho na planilha aberta no Windows — e um relatório que erra
  // a acentuação na primeira linha perde a credibilidade nas outras.
  const url = URL.createObjectURL(new Blob([BOM + csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `IDC ${report.store.protheusRef.replace('/', '-')} ${report.period.competence}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * `/relatorio/:storeId/:y/:m` — o relatório da competência (PLAN §7.1).
 *
 * É o artefato de auditoria, e a tela principal do fornecedor. A pergunta que
 * ela responde não é "qual foi a nota", e sim **"por que foi essa"**: cada
 * índice traz a conta aberta ao lado, e cada item que ficou de fora traz o
 * motivo em português.
 *
 * A URL leva o `storeId` — o plano previa `/relatorio/:y/:m`, resolvendo a loja
 * pelo seletor do cabeçalho. Com o id explícito a mesma tela serve ao fornecedor
 * e à Weloze sem estado escondido, e o isolamento continua no servidor: loja
 * fora do escopo responde 404, trocar o id na URL não abre nada.
 *
 * `print:` em toda a folha: imprimir gera o A4 sem biblioteca de PDF. O
 * navegador já sabe paginar — o que faltava era a folha dizer o que esconder.
 */

export function ReportPage() {
  const { storeId, year, month } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSupplier = user?.role === UserRole.SUPPLIER_USER;

  const [report, setReport] = useState<EvaluationReportDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acking, setAcking] = useState(false);
  const [competencias, setCompetencias] = useState<CompetenceOption[]>([]);

  const load = useCallback(async () => {
    setError(null);
    try {
      setReport(
        await api.get<EvaluationReportDto>(
          `/stores/${storeId}/evaluations/${year}/${month}/report`,
        ),
      );
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Não foi possível carregar o relatório desta competência.',
      );
    }
  }, [storeId, year, month]);

  useEffect(() => {
    void load();
  }, [load]);

  // O seletor é carregado à parte: falhar aqui não pode impedir a leitura do
  // relatório, que é o que a pessoa veio ver.
  useEffect(() => {
    void (async () => {
      try {
        setCompetencias(await api.get<CompetenceOption[]>(`/stores/${storeId}/periods`));
      } catch {
        setCompetencias([]);
      }
    })();
  }, [storeId]);

  usePageLabel(report ? `Relatório de ${report.period.label}` : null);

  /*
   * Abre os `<details>` para imprimir, e fecha de volta ao sair.
   *
   * Os itens excluídos do IDC são exatamente o que o fornecedor contesta, e não
   * podem sair recolhidos no papel. A regra de CSS que tentava fazer isso não
   * funcionava: o navegador esconde o interior de um `<details>` fechado por
   * `content-visibility`, e forçar `display` no filho dava altura sem
   * visibilidade. Mexer no atributo `open` é o único caminho que vale em todos
   * os navegadores — e o `<details>` aqui não é controlado pelo React, então
   * ninguém briga pelo estado.
   */
  useEffect(() => {
    const abertosPorNos = new Set<HTMLDetailsElement>();

    const abrir = () => {
      document.querySelectorAll('details').forEach((bloco) => {
        if (!bloco.open) {
          bloco.open = true;
          abertosPorNos.add(bloco);
        }
      });
    };
    const restaurar = () => {
      abertosPorNos.forEach((bloco) => {
        bloco.open = false;
      });
      abertosPorNos.clear();
    };

    window.addEventListener('beforeprint', abrir);
    window.addEventListener('afterprint', restaurar);
    return () => {
      window.removeEventListener('beforeprint', abrir);
      window.removeEventListener('afterprint', restaurar);
    };
  }, []);

  async function acknowledge() {
    if (!report?.evaluation.id) return;
    setAcking(true);
    try {
      await api.post(`/evaluations/${report.evaluation.id}/acknowledge`, {});
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível registrar a ciência.');
    } finally {
      setAcking(false);
    }
  }

  if (error) return <Alert>{error}</Alert>;

  if (!report) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-96" />
        <Skeleton className="h-28" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const { evaluation, period, store } = report;
  const naoAplicavel = evaluation.iqw === null;

  return (
    <div className="space-y-6 print:space-y-4">
      {/* 1. Cabeçalho ---------------------------------------------------- */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-slate-500">
            Relatório da competência
          </p>
          <h1 className="mt-0.5 text-2xl font-semibold tracking-tight text-slate-900">
            {store.supplierName}
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 text-sm text-slate-600">
            <span className="font-mono text-xs">{store.protheusRef}</span>
            {store.city && (
              <span>
                {store.city}
                {store.state && `/${store.state}`}
              </span>
            )}
            <span className="font-medium text-slate-800">{period.label}</span>
            <Badge
              tone={
                period.status === PeriodStatus.PUBLISHED
                  ? 'success'
                  : period.status === PeriodStatus.CLOSED
                    ? 'warning'
                    : 'neutral'
              }
            >
              {PERIOD_STATUS_LABEL[period.status]}
            </Badge>
          </p>
          {period.publishedAt && (
            <p className="mt-1 text-xs text-slate-500">
              Publicada em {formatDateTime(period.publishedAt)}
              {period.publishedByName && ` por ${period.publishedByName}`}.
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-2 print:hidden">
          {competencias.length > 1 && (
            <Select
              label="Competência"
              name="competencia"
              className="w-44"
              value={`${year}-${String(month).padStart(2, '0')}`}
              onChange={(e) => {
                const [ano, mes] = e.target.value.split('-');
                navigate(`/relatorio/${storeId}/${ano}/${Number(mes)}`);
              }}
            >
              {competencias.map((opcao) => (
                <option key={opcao.competence} value={opcao.competence}>
                  {opcao.label}
                </option>
              ))}
            </Select>
          )}
          <Button variant="outline" onClick={() => baixarCsv(report)}>
            <Sheet className="size-4" aria-hidden />
            Itens em Excel
          </Button>
          <Button variant="outline" onClick={() => window.print()}>
            <Printer className="size-4" aria-hidden />
            Imprimir / PDF
          </Button>
          {isSupplier && !evaluation.acknowledgedAt && (
            <Button loading={acking} onClick={() => void acknowledge()}>
              <CircleCheck className="size-4" aria-hidden />
              Dar ciência
            </Button>
          )}
        </div>
      </header>

      {/* O aviso é para todos, e por isso lê de `evaluation`, e não da trilha:
          competência alterada após a publicação aparece em tela e no papel, com
          o motivo (CLAUDE.md). Só o nome de quem recalculou é trilha — e ele
          chega vazio para quem não é master. */}
      {evaluation.alteredAfterPublication && (
        <div className="rounded-xl border border-red-300 bg-red-50 p-4">
          <p className="flex items-center gap-2 font-medium text-red-900">
            <TriangleAlert className="size-4 shrink-0" aria-hidden />
            Competência alterada após a publicação
          </p>
          <p className="mt-1 text-sm text-red-900/90">{evaluation.alterationReason}</p>
          <p className="mt-1 text-xs text-red-900/70">
            Recalculada
            {evaluation.alteredByName && ` por ${evaluation.alteredByName}`}
            {evaluation.alteredAt && ` em ${formatDateTime(evaluation.alteredAt)}`}. A versão
            anterior foi preservada.
          </p>
        </div>
      )}

      {evaluation.acknowledgedAt && (
        <p className="flex items-center gap-2 text-sm text-emerald-700">
          <CircleCheck className="size-4" aria-hidden />
          Ciência registrada em {formatDateTime(evaluation.acknowledgedAt)}.
        </p>
      )}

      {/* 2. IQW com a conta aberta ---------------------------------------- */}
      {naoAplicavel ? (
        /*
         * Só alcança avaliações gravadas antes de 08/2026, quando o mês sem item
         * encerrado não gerava nota nenhuma. A regra caiu (PLAN §3.5) e o
         * fechamento não produz mais isto — mas o relatório de uma competência
         * ainda não recalculada precisa continuar se explicando.
         */
        <Card>
          <h2 className="font-medium text-slate-900">Avaliação anterior à regra atual</h2>
          <p className="mt-1 text-sm text-slate-600">
            {evaluation.calculation.notApplicableReason}
          </p>
          <p className="mt-2 text-sm text-slate-600">
            Esta competência foi calculada quando o mês sem item de pedido encerrado não gerava
            avaliação. Hoje esse mês é avaliado com o IDC em 100%, e o IQW sai dos três índices.
            Recalcular a competência aplica a regra atual e preserva esta versão.
          </p>
        </Card>
      ) : (
        <IqwCard report={report} />
      )}

      {/* 3, 4, 5. Por que cada índice ------------------------------------- */}
      <IdcSection report={report} />
      <IqaSection report={report} />
      <IsqSection report={report} />

      {/* 6 e 7. Parâmetros e trilha --------------------------------------- */}
      <ParametersSection report={report} />
      {/* Só para usuário master, e quem decide é o servidor: para os demais a
          trilha nem vem no JSON (decisão do Vinicius, 09/2026). */}
      {report.trail && <TrailSection trail={report.trail} />}

      {/*
        Identificação no papel. A folha impressa circula solta — e uma página
        arrancada de um relatório sem dizer de quem é, de que mês e de quando
        não serve numa auditoria.
      */}
      <p className="hidden text-xs text-slate-500 print:block">
        {store.supplierName} · {store.protheusRef} · {period.label} · Portal do Fornecedor Weloze ·
        impresso em {formatDateTime(new Date().toISOString())}
      </p>
    </div>
  );
}

const TONE: Record<Classification, 'success' | 'warning' | 'danger' | 'neutral'> = {
  EXCELLENT: 'success',
  GOOD: 'warning',
  UNSATISFACTORY: 'danger',
  NOT_APPLICABLE: 'neutral',
};

/** O IQW com a conta aberta. Ninguém deve precisar de calculadora para conferir. */
function IqwCard({ report }: { report: EvaluationReportDto }) {
  const { evaluation, parameters } = report;

  const { unmeasured, iqwBasis } = evaluation.calculation;
  const semApuracao = new Set(unmeasured.map((index) => index.index));

  const parcelas = [
    { sigla: 'IDC' as const, valor: evaluation.idc, peso: parameters.weights.idc },
    { sigla: 'IQA' as const, valor: evaluation.iqa, peso: parameters.weights.iqa },
    { sigla: 'ISQ' as const, valor: evaluation.isq, peso: parameters.weights.isq },
  ];

  // A soma dos pesos apurados e o divisor da renormalizacao, e e o que faz a
  // formula fechar na calculadora de quem confere.
  const base = parcelas
    .filter((parcela) => !semApuracao.has(parcela.sigla))
    .reduce((total, parcela) => total + parcela.peso, 0);

  return (
    <Card>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">IQW da competência</p>
          <p className="mt-1 text-4xl font-semibold tabular-nums text-slate-900">
            {formatIndex(evaluation.iqw)}
          </p>
        </div>
        <Badge tone={TONE[evaluation.classification]} className="text-sm">
          {CLASSIFICATION_LABEL[evaluation.classification]}
        </Badge>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {parcelas.map((p) => {
          const faltou = semApuracao.has(p.sigla);
          return (
            <div
              key={p.sigla}
              className={
                faltou
                  ? 'rounded-lg border border-dashed border-slate-300 bg-slate-50/60 px-3 py-2'
                  : 'rounded-lg border border-slate-200 px-3 py-2'
              }
            >
              <p className="text-xs uppercase tracking-wide text-slate-500">
                {p.sigla} ·{' '}
                {faltou
                  ? 'sem apuração'
                  : base === 1
                    ? `peso ${formatDecimal(p.peso)}`
                    : `peso ${formatDecimal(p.peso)} ÷ ${formatDecimal(base)}`}
              </p>
              <p className="mt-0.5 text-xl font-semibold tabular-nums text-slate-800">
                {faltou ? <span className="text-slate-400">—</span> : formatIndex(p.valor)}
              </p>
            </div>
          );
        })}
      </div>

      {/* A frase vem do servidor, congelada no fechamento: o relatório precisa
          dizer com todas as letras sobre o que a nota foi calculada. */}
      {unmeasured.length > 0 && (
        <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {iqwBasis}
        </p>
      )}

      {evaluation.calculation.iqwFormula && (
        <p className="mt-3 overflow-x-auto rounded-lg bg-slate-50 px-3 py-2 font-mono text-sm text-slate-700">
          {evaluation.calculation.iqwFormula}
        </p>
      )}
      <p className="mt-2 text-xs text-slate-500">
        A soma usa os índices já arredondados, para a conta fechar exatamente como está escrita.
        Faixas: Excelente a partir de {formatDecimal(parameters.bands.excellent)}, Bom a partir de{' '}
        {formatDecimal(parameters.bands.good)}.
      </p>
    </Card>
  );
}

function Section({
  title,
  summary,
  children,
}: {
  title: string;
  summary: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="break-inside-avoid">
      <h2 className="font-medium text-slate-900">{title}</h2>
      <p className="mt-1 text-sm text-slate-600">{summary}</p>
      <div className="mt-3">{children}</div>
    </Card>
  );
}

/** 3. O IDC item de pedido a item de pedido. */
function IdcSection({ report }: { report: EvaluationReportDto }) {
  const { calculation } = report.evaluation;
  const { counted, excluded, renegotiated } = report.idc;

  return (
    <Section
      title="IDC — cumprimento de prazo"
      summary={
        calculation.idcItemsTotal > 0
          ? `${calculation.idcItemsOnTime} de ${calculation.idcItemsTotal} itens de pedido no prazo.` +
            (calculation.idcItemsExcluded > 0
              ? ` ${calculation.idcItemsExcluded} item(ns) não entraram na conta — a lista está no fim desta seção.`
              : '')
          : report.evaluation.idc === null
            ? // Avaliação congelada com a v1 (regra de 08/2026): continua dizendo
              // o que foi feito com ela, e não o que a regra de hoje faria.
              'Nenhum item de pedido encerrado nesta competência: o IDC não foi apurado, e não vale ' +
              'zero nem cem. O IQW saiu dos demais índices.'
            : // Regra vigente (v2, 09/2026). Sem a frase, "100%" ao lado de zero
              // itens parece defeito — e é justamente o número que alguém contesta.
              'Nenhum item de pedido encerrado nesta competência. Sem entrega a avaliar, não há ' +
              `atraso a descontar: pela regra do conjunto, o IDC vale ${formatIndex(report.evaluation.idc)}.`
      }
    >
      {counted.length > 0 && <ItemsTable items={counted} />}

      {excluded.length > 0 && (
        <details className="mt-4 rounded-lg border border-slate-200">
          <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-700">
            {excluded.length} item(ns) fora da conta, com o motivo
          </summary>
          <div className="border-t border-slate-200 p-3">
            <ItemsTable items={excluded} showReason />
          </div>
        </details>
      )}

      {renegotiated.length > 0 && (
        <details className="mt-2 rounded-lg border border-slate-200">
          <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-700">
            {renegotiated.length} item(ns) com data prevista renegociada
          </summary>
          <div className="border-t border-slate-200 p-3">
            <p className="mb-2 flex items-start gap-2 text-xs text-slate-500">
              <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              A renegociação de data é informativa e <strong>não entra na nota</strong>. A conta usa
              a data prevista vigente no encerramento.
            </p>
            <ItemsTable items={renegotiated} />
          </div>
        </details>
      )}
    </Section>
  );
}

function ItemsTable({ items, showReason }: { items: SnapshotItemDto[]; showReason?: boolean }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full min-w-[46rem] text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50/70 text-left text-xs uppercase tracking-wide text-slate-500">
            <th className="px-3 py-2 font-medium">Pedido</th>
            <th className="px-3 py-2 font-medium">Produto</th>
            <th className="px-3 py-2 font-medium">Previsto</th>
            <th className="px-3 py-2 font-medium">Entrega</th>
            <th className="px-3 py-2 font-medium">Diferença</th>
            <th className="px-3 py-2 font-medium">{showReason ? 'Motivo' : 'Notas fiscais'}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((item) => (
            <tr key={`${item.orderNumber}-${item.orderItem}`}>
              <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-slate-600">
                {item.orderNumber}/{item.orderItem}
              </td>
              <td className="px-3 py-2">
                <span className="font-mono text-xs text-slate-500">{item.productCode}</span>
                {item.productDesc && (
                  <p className="text-xs text-slate-600">{item.productDesc}</p>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                {formatDate(item.expectedDate)}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                {formatDate(item.deliveryDate)}
                {item.deliveryDateSource === 'DTDIGIT' && (
                  <p className="text-xs text-slate-400">pela digitação da nota</p>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-2">
                {/* Nunca o número cru: "−6" não diz se é atraso ou antecipação. */}
                <span className={item.onTime === false ? 'text-red-600' : 'text-slate-700'}>
                  {item.diffLabel}
                </span>
              </td>
              <td className="px-3 py-2 text-xs text-slate-600">
                {showReason
                  ? (item.exclusionReason && IDC_EXCLUSION_LABEL[item.exclusionReason]) || '—'
                  : item.invoiceRefs.join(', ') || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** 4. O IQA, RNC a RNC. */
function IqaSection({ report }: { report: EvaluationReportDto }) {
  const { calculation, iqa } = report.evaluation;
  const rncs = report.iqa.rncs;

  return (
    <Section
      title="IQA — qualidade assegurada"
      summary={
        rncs.length === 0
          ? 'Nenhuma RNC emitida nesta competência — o índice fica em 100%.'
          : `100% − ${formatDecimal(calculation.iqaTotalDemerit)}% de demérito = ${formatIndex(iqa)}.` +
            (calculation.iqaFlooredAtZero
              ? ' A soma dos deméritos passou de 100%, e o índice parou no piso de 0%.'
              : '')
      }
    >
      {rncs.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full min-w-[44rem] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/70 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2 font-medium">RNC</th>
                <th className="px-3 py-2 font-medium">Emissão</th>
                <th className="px-3 py-2 font-medium">Gravidade</th>
                <th className="px-3 py-2 text-right font-medium">Demérito</th>
                <th className="px-3 py-2 font-medium">Descrição</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rncs.map((rnc) => (
                <tr key={rnc.id}>
                  <td className="px-3 py-2">
                    {/* Leva ao 8D. Quem lê o relatório e vê "−50%" quer saber o
                        que aconteceu, e antes disso ficava sem caminho. */}
                    <Link
                      to={`/rncs/${rnc.id}`}
                      className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs font-medium text-slate-800 underline-offset-4 hover:bg-slate-200 hover:underline print:no-underline"
                    >
                      {rnc.number}
                    </Link>
                    {rnc.acknowledgedAt && (
                      <p className="mt-0.5 text-xs text-emerald-700">ciência registrada</p>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                    {formatDate(rnc.issuedAt)}
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    {RNC_SEVERITY_LABEL[rnc.severity]}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-600">
                    −{formatDecimal(rnc.demeritPercent)}%
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {rnc.description}
                    {(rnc.productCode || rnc.invoiceRef) && (
                      <p className="mt-0.5 font-mono text-xs text-slate-500">
                        {[rnc.productCode, rnc.invoiceRef].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

/** 5. O ISQ: qual certificado determinou, e os que não contaram. */
function IsqSection({ report }: { report: EvaluationReportDto }) {
  const { isq } = report;

  return (
    <Section
      title="ISQ — sistema da qualidade"
      summary={`${isq.explanation}. Demérito de ${formatDecimal(isq.demeritPercent)}%, apurado em ${formatDate(isq.referenceDate)} — o último dia da competência.`}
    >
      {isq.counted.length > 0 && (
        <ul className="space-y-1 text-sm">
          {isq.counted.map((cert) => (
            <li key={cert.id} className="flex flex-wrap items-center gap-x-2 text-slate-700">
              <BadgeCheck className="size-4 shrink-0 text-emerald-600" aria-hidden />
              <span className="font-medium">{cert.standardLabel}</span>
              {cert.number && <span className="font-mono text-xs text-slate-500">{cert.number}</span>}
              <span className="text-slate-500">válido até {formatDate(cert.validUntil)}</span>
            </li>
          ))}
        </ul>
      )}

      {isq.notCounted.length > 0 && (
        <div className="mt-3 rounded-lg border border-slate-200 p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Certificados que não contaram
          </p>
          <ul className="mt-2 space-y-1 text-sm text-slate-600">
            {isq.notCounted.map((cert) => (
              <li key={cert.id} className="flex flex-wrap gap-x-2">
                <span className="font-medium text-slate-800">{cert.standardLabel}</span>
                <span className="text-slate-500">— {cert.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isq.counted.length === 0 && isq.notCounted.length === 0 && (
        <p className="text-sm text-slate-600">
          Nenhum certificado cadastrado para esta loja.
        </p>
      )}

      {isq.ladder.length > 0 && <IsqLadder steps={isq.ladder} />}
    </Section>
  );
}

/**
 * A escada do ISQ inteira, com o degrau atual e o seguinte marcados.
 *
 * É a régua. Quem lê "20% de demérito" sem ela não sabe o que fazer para
 * melhorar, e um índice que não ensina a melhorar não muda comportamento
 * nenhum. Os degraus vêm dos parâmetros congelados, e não de uma lista escrita
 * aqui: mudar um demérito muda a régua junto, inclusive num relatório antigo,
 * que continua mostrando a régua de então.
 */
function IsqLadder({ steps }: { steps: EvaluationReportDto['isq']['ladder'] }) {
  return (
    <div className="mt-4 break-inside-avoid">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">A régua do ISQ</p>
      <ul className="mt-2 divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200">
        {steps.map((step) => (
          <li
            key={step.label}
            className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm ${
              step.current ? 'bg-brand-50' : step.next ? 'bg-emerald-50/60' : ''
            }`}
          >
            <span className="w-14 shrink-0 text-right font-semibold tabular-nums text-slate-800">
              {formatDecimal(step.isqValue)}
            </span>
            <span className="flex-1 text-slate-700">{step.label}</span>
            <span className="text-xs text-slate-500">
              demérito {formatDecimal(step.demeritPercent)}%
            </span>
            {step.current && <Badge tone="neutral">você está aqui</Badge>}
            {step.next && <Badge tone="success">próximo degrau</Badge>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** 6. Os parâmetros como estavam no fechamento. */
function ParametersSection({ report }: { report: EvaluationReportDto }) {
  const p = report.parameters;
  const linhas = [
    ['Pesos', `IDC ${formatDecimal(p.weights.idc)} · IQA ${formatDecimal(p.weights.iqa)} · ISQ ${formatDecimal(p.weights.isq)}`],
    ['Faixas', `Excelente ≥ ${formatDecimal(p.bands.excellent)} · Bom ≥ ${formatDecimal(p.bands.good)} · abaixo, Insatisfatório`],
    ['Tolerância de atraso', `${p.toleranceDays} dias`],
    ['Arredondamento', p.rounding],
    ['Conjunto de parâmetros', `versão ${p.version}`],
  ];

  return (
    <Section
      title="Parâmetros congelados"
      summary="Os valores que valiam no fechamento. Alterá-los hoje não muda este relatório."
    >
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        {linhas.map(([termo, valor]) => (
          <div key={termo}>
            <dt className="text-xs uppercase tracking-wide text-slate-500">{termo}</dt>
            <dd className="text-slate-700">{valor}</dd>
          </div>
        ))}
      </dl>

      {/* Os mesmos números, escritos como regra. A lista acima serve para
          conferir; estas frases servem para saber o que fazer. */}
      {p.conditions.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            O que este conjunto determina
          </p>
          <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
            {p.conditions.map((condicao) => (
              <li key={condicao} className="flex gap-2">
                <span aria-hidden className="mt-2 size-1 shrink-0 rounded-full bg-slate-400" />
                <span>{condicao}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Section>
  );
}

/** 7. A trilha de auditoria — só para usuário master. */
function TrailSection({ trail: t }: { trail: ReportTrailDto }) {
  const eventos: string[] = [];

  if (t.closedAt) {
    eventos.push(
      `Competência fechada em ${formatDateTime(t.closedAt)}${t.closedByName ? ` por ${t.closedByName}` : ''}.`,
    );
  }
  if (t.publishedAt) {
    eventos.push(
      `Publicada em ${formatDateTime(t.publishedAt)}${t.publishedByName ? ` por ${t.publishedByName}` : ''}.`,
    );
  }
  if (t.alteredAt) {
    eventos.push(
      `Recalculada em ${formatDateTime(t.alteredAt)}${t.alteredByName ? ` por ${t.alteredByName}` : ''} — ${t.alterationReason}`,
    );
  }
  if (t.acknowledgedAt) {
    eventos.push(`Ciência do fornecedor registrada em ${formatDateTime(t.acknowledgedAt)}.`);
  }

  return (
    <Section title="Trilha" summary="Quem fez o quê, e quando.">
      <ul className="space-y-1 text-sm text-slate-600">
        {eventos.map((evento) => (
          <li key={evento}>{evento}</li>
        ))}
      </ul>

      {t.imports.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Importações que cobriram esta competência
          </p>
          <ul className="mt-1 space-y-0.5 text-xs text-slate-500">
            {t.imports.map((run) => (
              <li key={run.id}>
                {formatDateTime(run.startedAt)} — janela de {formatDate(run.fromDate)} a{' '}
                {formatDate(run.toDate)}, {run.itemsClosed} itens encerrados
              </li>
            ))}
          </ul>
        </div>
      )}
    </Section>
  );
}

/** Atalho para o relatório, usado pelo painel e pela ficha do fornecedor. */
export function ReportLink({
  storeId,
  competence,
  children,
  className,
}: {
  storeId: string;
  competence: string;
  children: React.ReactNode;
  className?: string;
}) {
  const [year, month] = competence.split('-');
  return (
    <Link to={`/relatorio/${storeId}/${year}/${Number(month)}`} className={className}>
      {children}
    </Link>
  );
}
